import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
} from "@mui/icons-material";
import { bookingService } from "../services/bookingService";

const defaultCarnet = {
  name: "",
  price: "",
};

function CarnetManager() {
  const [carnets, setCarnets] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalEdit, setModalEdit] = useState(false);
  const [modalCarnet, setModalCarnet] = useState(defaultCarnet);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    loadCarnets();
  }, []);

  const loadCarnets = async () => {
    try {
      setLoading(true);
      const data = await bookingService.getCarnets();
      setCarnets(data);
    } catch (error) {
      console.error("Error loading carnets:", error);
      setError("Errore nel caricamento dei carnet");
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setModalCarnet(defaultCarnet);
    setModalEdit(false);
    setModalOpen(true);
    setError("");
    setSuccess("");
  };

  const handleEdit = (carnet) => {
    setModalCarnet({ ...carnet });
    setModalEdit(true);
    setModalOpen(true);
    setError("");
    setSuccess("");
  };

  const handleClose = () => {
    setModalOpen(false);
    setModalCarnet(defaultCarnet);
    setError("");
    setSuccess("");
  };

  const handleInputChange = (field, value) => {
    setModalCarnet((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const validateForm = () => {
    if (!modalCarnet.name.trim()) {
      setError("Il nome del pacchetto è obbligatorio");
      return false;
    }
    if (modalCarnet.price <= 0) {
      setError("Il prezzo deve essere maggiore di 0");
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    try {
      setLoading(true);
      setError("");

      if (modalEdit) {
        await bookingService.updateCarnet(modalCarnet.id, modalCarnet);
        setSuccess("Pacchetto aggiornato con successo!");
      } else {
        await bookingService.createCarnet(modalCarnet);
        setSuccess("Pacchetto creato con successo!");
      }

      await loadCarnets();
      handleClose();
    } catch (error) {
      console.error("Error saving Pacchetto:", error);
      setError("Errore nel salvataggio del Pacchetto");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Sei sicuro di voler eliminare questo Pacchetto?")) {
      return;
    }

    try {
      setLoading(true);
      await bookingService.deleteCarnet(id);
      await loadCarnets();
      setSuccess("Pacchetto eliminato con successo!");
    } catch (error) {
      console.error("Error deleting Pacchetto:", error);
      setError("Errore nell'eliminazione del Pacchetto");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h6">Gestione Pacchetti</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreate}
          disabled={loading}
        >
          Aggiungi Pacchetto
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Nome Pacchetto</TableCell>
              <TableCell align="right">Prezzo</TableCell>
              <TableCell align="right">Azioni</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {carnets.length === 0 && !loading && (
              <TableRow>
                <TableCell colSpan={3} align="center">
                  <Typography color="textSecondary">
                    Nessun Pacchetto configurato. Creane uno per iniziare.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {carnets.map((carnet) => (
              <TableRow key={carnet.id}>
                <TableCell>{carnet.name}</TableCell>
                <TableCell align="right">€{carnet.price}</TableCell>
                <TableCell align="right">
                  <IconButton
                    size="small"
                    onClick={() => handleEdit(carnet)}
                    disabled={loading}
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleDelete(carnet.id)}
                    disabled={loading}
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create/Edit Modal */}
      <Dialog open={modalOpen} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          {modalEdit ? "Modifica Pacchetto" : "Nuovo Pacchetto"}
        </DialogTitle>

        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Nome del Pacchetto"
              value={modalCarnet.name}
              onChange={(e) => handleInputChange("name", e.target.value)}
              required
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              label="Prezzo (€)"
              type="number"
              value={modalCarnet.price}
              onChange={(e) =>
                handleInputChange("price", parseFloat(e.target.value) || 0)
              }
              required
              InputProps={{ inputProps: { min: 0, step: 0.01 } }}
            />
          </Box>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={handleClose}
            startIcon={<CancelIcon />}
            disabled={loading}
          >
            Annulla
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            startIcon={<SaveIcon />}
            disabled={loading}
          >
            {loading ? "Salvataggio..." : "Salva"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default CarnetManager;
